/**
 * Encoder real OGG/Opus via WebAssembly (opus-recorder).
 *
 * Estratégia:
 * - Usa opus-recorder para produzir OGG/Opus em qualquer navegador moderno
 * - Não depende do MediaRecorder nativo para o formato final
 * - Se o encoder WASM falhar, bloqueia envio com erro claro
 */

import Recorder from 'opus-recorder';
import encoderPath from 'opus-recorder/dist/encoderWorker.min.js?url';

/**
 * Cria uma instância do opus-recorder configurada para voice note WhatsApp.
 * O recorder já faz getUserMedia internamente, mas aqui passamos o stream
 * para manter controle do ciclo de vida.
 */
export function createOggRecorder(): Recorder {
  return new Recorder({
    encoderPath,
    encoderApplication: 2048, // VOIP — otimizado para voz
    numberOfChannels: 1,
    encoderSampleRate: 48000,
    streamPages: true, // emite páginas OGG incrementais via ondataavailable
  });
}

/**
 * Inicia gravação e retorna Promise que resolve com o File .ogg ao parar.
 * 
 * Uso:
 *   const recorder = createOggRecorder();
 *   const filePromise = startOggRecording(recorder);
 *   // ... quando quiser parar:
 *   await recorder.stop();
 *   const file = await filePromise;
 */
export function startOggRecording(recorder: Recorder): Promise<File | null> {
  return new Promise<File | null>((resolve) => {
    const pages: ArrayBuffer[] = [];

    recorder.ondataavailable = (arrayBuffer: ArrayBuffer) => {
      pages.push(arrayBuffer);
    };

    recorder.onstop = () => {
      if (pages.length === 0) {
        console.error('[audioConverter] ❌ Nenhum dado OGG recebido do encoder');
        resolve(null);
        return;
      }

      const blob = new Blob(pages, { type: 'audio/ogg' });
      if (blob.size === 0) {
        console.error('[audioConverter] ❌ Blob OGG vazio');
        resolve(null);
        return;
      }

      const file = new File([blob], `voice_${Date.now()}.ogg`, { type: 'audio/ogg' });
      console.log(`[audioConverter] ✅ OGG/Opus gerado via WASM: ${file.size} bytes, ${pages.length} páginas`);
      resolve(file);
    };
  });
}

/**
 * Valida se um arquivo de áudio enviado manualmente é de formato suportado pelo WhatsApp.
 * Formatos aceitos: .ogg, .mp3, .m4a, .aac, .amr
 * Rejeita: .webm
 */
export function isManualAudioSupported(fileName: string, mimeType: string): boolean {
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  const allowedExtensions = ['ogg', 'mp3', 'm4a', 'aac', 'amr', 'mpeg', 'mp4'];
  const blockedExtensions = ['webm'];

  if (blockedExtensions.includes(ext)) return false;
  if (allowedExtensions.includes(ext)) return true;

  // Fallback por MIME
  const mime = mimeType.toLowerCase();
  if (mime.includes('webm')) return false;
  if (
    mime.includes('ogg') ||
    mime.includes('mp3') ||
    mime.includes('mpeg') ||
    mime.includes('mp4') ||
    mime.includes('aac') ||
    mime.includes('amr')
  ) return true;

  return false;
}
